import React, { useState } from 'react';
import { deliveryAPI } from '../../../services/deliveryAPI';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { shouldShowFormType } from './feedConstants';
import FeedProductPicker, {
  FeedSelection, emptyFeedSelection, isFeedSelectionValid,
} from './FeedProductPicker';
import { hasFeedChoice } from './resolveFeedProduct';
import '../../../pages/BridgeClub/BridgeClubFeed.css';

// The Add Feed dialog only needs these three fields off the target bin. The Feed
// page passes its full FeedBin, which is structurally compatible.
interface AddFeedTargetBin {
  id: number;
  name: string;
  feed_category: string;
}

interface AddFeedDialogProps {
  bin: AddFeedTargetBin;
  onClose: () => void;
  onAdded: () => void;
  onError?: (message: string) => void;
}

/**
 * Add Feed dialog. The branded product picker now lives in the shared FeedProductPicker
 * (used by the onboarding step too, so the catalog can't drift); this component keeps the
 * modal chrome and the add-feed call. Behaviour is unchanged from the extracted version:
 * same catalog, same Red-Bridge-first grouping, same auto-filled bag size, same form type.
 */
const AddFeedDialog: React.FC<AddFeedDialogProps> = ({ bin, onClose, onAdded, onError }) => {
  const [sel, setSel] = useState<FeedSelection>(emptyFeedSelection);
  const [saving, setSaving] = useState(false);
  // Server-owned copy about deliverability, shown after an adoption.
  const [notice, setNotice] = useState<string | null>(null);

  const handleAddFeed = async () => {
    // Form type is required for most categories, but not for cat/dog.
    const requiresFormType = shouldShowFormType(bin.feed_category);
    if (!isFeedSelectionValid(sel, bin.feed_category)) return;
    setSaving(true);
    // Declared outside the try so the post-save branch below can read it.
    let adoptedNotice: string | null = null;
    try {
      // ADOPT AT SAVE. A catalog product picked by search or scan has no
      // FeedProduct row yet - minting on selection would litter the table with
      // bags nobody chose. Adoption mints it, promotes it to a sellable bag and
      // maps the two, then tells us whether the result is actually deliverable.
      //
      // This is what stops the free-text defect: without a feed_product_id the
      // backend leaves FeedBin.feed_product null, and store_product_for_bin
      // returns None on a null feed_product - an invisible undeliverable bin.
      let feedProductId = sel.selectedProductId ?? undefined;
      if (!feedProductId && sel.catalogProductId) {
        const adopted = await deliveryAPI.adoptCatalogProduct(sel.catalogProductId);
        feedProductId = adopted.feed_product_id ?? undefined;
        // Only worth stopping for when there is something they need to know.
        // A bag that is ready to deliver does not need a confirmation screen.
        if (!adopted.deliverable) adoptedNotice = adopted.message;
      }

      await bridgeClubAPI.addFeedToBin(bin.id, {
        lbs: parseFloat(sel.amount),
        product_name: sel.productName || 'Manual Addition',
        form_type: requiresFormType ? sel.formType : undefined,
        // The branded-catalog FeedProduct id (null for a custom "Other" entry). The
        // backend sets FeedBin.feed_product from it — the only reason bins are deliverable.
        feed_product_id: feedProductId,
      });
    } catch (err) {
      console.error('Failed to add feed:', err);
      onError?.('Failed to add feed to bin');
      setSaving(false);
      return;
    }
    onAdded();
    // HOLD THE DIALOG OPEN when the bag needs setting up. onClose() here would
    // close over the message before the customer could read it, and "we need to
    // set this up first" is exactly the thing they must not miss.
    if (adoptedNotice) {
      setNotice(adoptedNotice);
      setSaving(false);
      return;
    }
    onClose();
  };

  // THE SAME DIALOG AS "CHANGE WHAT WE DELIVER" (Brad, 2026-09-22: "It should be
  // identical to what we just built in subscription change. This one has double
  // sliders in it like we fixed already."). Centred above the nav, three parts, only
  // the product list scrolls; the amount sits in the pinned footer beside the buttons.
  const muted: React.CSSProperties = { color: '#6b7280', fontSize: 13 };
  return (
    <div
      className="modal-overlay"
      onClick={() => { if (!saving) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 16px 100px' }}
    >
      <div
        className="modal-content modal-add-feed"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 140px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid #eee', flex: '0 0 auto' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#8B0000' }}>{bin.name}: add feed</h3>
          <p style={{ ...muted, margin: '2px 0 0' }}>Feed you bought and put in the bin.</p>
        </div>

        {notice ? (
          <div style={{ padding: '12px 16px', flex: '1 1 auto', overflowY: 'auto' }}>
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#78350f' }}>
              <p style={{ margin: '0 0 10px' }}>{notice}</p>
              <button type="button" className="btn-primary" onClick={onClose} style={{ minHeight: 44 }}>Got it</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '8px 16px', overflow: 'hidden', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FeedProductPicker feedCategory={bin.feed_category} value={sel} onChange={setSel} hideAmount fillHeight />
          </div>
        )}

        {!notice && (
          <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #eee', background: '#fff', flex: '0 0 auto' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 14, fontWeight: 600, color: '#374151' }}>
              <span style={{ flex: '0 0 auto' }}>Amount (lb)</span>
              <input
                type="text"
                inputMode="decimal"
                value={sel.amount}
                onChange={(e) => setSel({ ...sel, amount: e.target.value })}
                placeholder="e.g. 40"
                aria-label="Amount (lb)"
                style={{ flex: 1, minHeight: 44, fontSize: 16, padding: '0 10px', borderRadius: 8, border: '1px solid #d1d5db' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-secondary" style={{ flex: 1, minHeight: 44 }} disabled={saving} onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                style={{ flex: 1, minHeight: 44 }}
                onClick={handleAddFeed}
                // One definition of "complete", shared with onboarding (Brad, 2026-09-22).
                disabled={saving || !hasFeedChoice(sel) || !isFeedSelectionValid(sel, bin.feed_category)}
              >
                {saving ? 'Adding...' : 'Add Feed'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddFeedDialog;
